import React from 'react'
import PropTypes from 'prop-types'
import { useFormSelect } from './FormSelect.logic'
import { FormSelectView } from './FormSelect.view'

/**
 * FormSelect Container Component
 * Select/dropdown field integrated with React Hook Form
 *
 * @example
 * // Static options
 * <FormSelect
 *   name="orderType"
 *   control={control}
 *   label="Order Type"
 *   options={[
 *     { key: 'delivery', value: 'delivery', text: 'Delivery' },
 *     { key: 'pickup', value: 'pickup', text: 'Pickup' }
 *   ]}
 *   required
 * />
 *
 * @example
 * // Async options with loading
 * <FormSelect
 *   name="market"
 *   control={control}
 *   label="Market"
 *   options={markets}
 *   loading={isLoadingMarkets}
 *   search
 * />
 *
 * @example
 * // Server-side search
 * <FormSelect
 *   name="place"
 *   control={control}
 *   label="Place"
 *   options={searchResults}
 *   loading={isSearching}
 *   search
 *   onSearchChange={(query) => setSearchQuery(query)}
 *   selectOnBlur={false}
 * />
 */
export function FormSelect({
  name,
  control,
  defaultValue,
  rules,
  label,
  placeholder,
  required,
  disabled,
  loading,
  search,
  clearable,
  multiple,
  fluid,
  selectOnBlur,
  options,
  onSearchChange,
  onChange: customOnChange,
  noOptionsMessage,
  filterOption,
  'data-testid': dataTestId,
  'data-automation-id': dataAutomationId,
}) {
  const {
    onChange,
    onBlur,
    onInputChange,
    inputValue,
    name: fieldName,
    ref,
    reactSelectOptions,
    reactSelectValue,
    error,
    errorMessage,
  } = useFormSelect({
    name,
    control,
    defaultValue,
    rules,
    onSearchChange,
    onChange: customOnChange,
    options,
    multiple,
  })

  return (
    <FormSelectView
      name={fieldName}
      onChange={onChange}
      onBlur={onBlur}
      onInputChange={onInputChange}
      inputValue={inputValue}
      selectRef={ref}
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      loading={loading}
      search={search}
      clearable={clearable}
      multiple={multiple}
      reactSelectOptions={reactSelectOptions}
      reactSelectValue={reactSelectValue}
      error={error}
      errorMessage={errorMessage}
      noOptionsMessage={noOptionsMessage}
      filterOption={filterOption}
      data-testid={dataTestId}
      data-automation-id={dataAutomationId}
    />
  )
}

FormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  defaultValue: PropTypes.any,
  rules: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  search: PropTypes.bool,
  clearable: PropTypes.bool,
  multiple: PropTypes.bool,
  fluid: PropTypes.bool,
  selectOnBlur: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      // Not `.isRequired`: that rejects null, and a sentinel option whose value
      // IS null is a legitimate shape (the accessorial stop select's
      // 'N/A (Not Applicable)' row, which stores no leg index).
      value: PropTypes.any,
      text: PropTypes.string.isRequired,
      image: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      // Secondary line under the option in the open menu (Semantic Dropdown parity)
      description: PropTypes.string,
      className: PropTypes.string,
    }),
  ).isRequired,
  onSearchChange: PropTypes.func,
  onChange: PropTypes.func,
  noOptionsMessage: PropTypes.func,
  filterOption: PropTypes.func,
  'data-testid': PropTypes.string,
  'data-automation-id': PropTypes.string,
}

FormSelect.defaultProps = {
  fluid: true,
  disabled: false,
  required: false,
  loading: false,
  search: false,
  clearable: false,
  multiple: false,
  selectOnBlur: true,
  defaultValue: '',
}
